import EdudoResponse from '../models/edudo-response';
import HttpService from './http-service';

export default class DiaryService {
  private schoolUuid: string;

  constructor(schoolUuid: string) {
    this.schoolUuid = schoolUuid;
  }

  private basePath(): string {
    return `/v1/schools/${this.schoolUuid}`;
  }

  private backofficeBasePath(): string {
    return `/school-backoffice-bff/v1/schools/${this.schoolUuid}`;
  }

  async listDiaryCounts(): Promise<ClassroomDiaryCount[]> {
    const response = await HttpService.getJson<EdudoResponse<ClassroomDiaryCount[]>>(
      `${this.backofficeBasePath()}/diary-counts`
    );

    return response.data;
  }

  async getClassroomOverview(classroomUuid: string): Promise<ClassroomDiaryOverview> {
    const response = await HttpService.getJson<EdudoResponse<ClassroomDiaryOverview>>(
      `${this.backofficeBasePath()}/classrooms/${classroomUuid}/diary-overview`
    );

    return response.data;
  }

  // Alunos cujo dia não tem diário nem falta; date omitida é o hoje da escola.
  async listPendingStudents(classroomUuid: string, date?: string): Promise<ClassroomPendingStudents> {
    const query = date ? `?date=${date}` : '';
    const response = await HttpService.getJson<EdudoResponse<ClassroomPendingStudents>>(
      `${this.backofficeBasePath()}/classrooms/${classroomUuid}/diary-pending-students${query}`
    );

    return response.data;
  }

  // Rascunhos primeiro; date omitida é o hoje da escola.
  async listDiaryGroups(classroomUuid: string, date?: string): Promise<ClassroomDiaryGroup[]> {
    const query = date ? `?date=${date}` : '';
    const response = await HttpService.getJson<EdudoResponse<ClassroomDiaryGroup[]>>(
      `${this.backofficeBasePath()}/classrooms/${classroomUuid}/diary-groups${query}`
    );

    return response.data;
  }

  async getClassroomDay(classroomUuid: string, day: string): Promise<ClassroomDiaryDay> {
    const response = await HttpService.getJson<EdudoResponse<ClassroomDiaryDay>>(
      `${this.backofficeBasePath()}/classrooms/${classroomUuid}/diary-days/${day}`
    );

    return response.data;
  }

  async listStudentRecords(studentUuid: string, offset = 0, limit = 10): Promise<StudentRecord[]> {
    const response = await HttpService.getJson<EdudoResponse<StudentRecord[]>>(
      `${this.backofficeBasePath()}/students/${studentUuid}/daily-records?offset=${offset}&limit=${limit}`
    );

    return response.data;
  }

  // O diário é sempre do hoje da escola e o modo vem da turma. Tudo ou nada: um dia já
  // ocupado, inclusive por um rascunho, devolve 409 e nenhum diário é salvo.
  async createDiaryGroup(data: CreateDiaryGroupRequest): Promise<DiaryGroup> {
    const response = await HttpService.postJson<EdudoResponse<DiaryGroup>>(`${this.basePath()}/diary-groups`, data);

    return response.data;
  }

  // Só no próprio dia do rascunho (400 Diary.NotPublishable); versão velha é 409 Diary.Changed.
  // Com studentUuids, separa e envia só esses alunos e devolve o grupo novo; o resto continua rascunho.
  async publishDiaryDraft(groupId: string, version: number, studentUuids?: string[]): Promise<DiaryGroup> {
    const response = await HttpService.postJson<EdudoResponse<DiaryGroup>>(
      `${this.basePath()}/diary-groups/${groupId}/publication`,
      studentUuids ? { version, studentUuids } : { version }
    );

    return response.data;
  }

  async editDiaryGroup(groupId: string, data: EditDiaryGroupRequest): Promise<DiaryGroup> {
    const response = await HttpService.patchJson<EdudoResponse<DiaryGroup>>(
      `${this.basePath()}/diary-groups/${groupId}`,
      data
    );

    return response.data;
  }

  async deleteDiaryEntry(entryId: string): Promise<void> {
    await HttpService.delete(`${this.basePath()}/diary-entries/${entryId}`);
  }

  // Tudo ou nada: o rascunho inteiro sai numa gravação só. Enviado é 409 Diary.AlreadyPublished; versão velha, 409 Diary.Changed.
  async discardDiaryDraft(groupId: string, version: number): Promise<void> {
    await HttpService.delete(`${this.basePath()}/diary-groups/${groupId}?version=${version}`);
  }
}
